import React, { useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';

/**
 * DataIssuesBanner — Surfaces per-row data issues from backend endpoints.
 *
 * Expects `issues` array of shape:
 *   [{ step, level, message, ref: { type, id, label, field, value } }]
 *
 * Renders a collapsible amber banner with one row per issue, including a
 * deep link to the offending record when we know how to route it
 * (sales_order → /sales · batch → /production · client → /clients · etc.).
 */
const TYPE_ROUTES = {
  sales_order: (ref) => `/bill-log`,           // closest "show invoices" page
  batch:       (ref) => `/production`,
  client:      (ref) => `/clients`,
  vendor:      (ref) => `/purchases`,
  purchase_bill: (ref) => `/purchases`,
  raw_material: (ref) => `/inventory`,
};

export default function DataIssuesBanner({ issues = [], warnings = [], onDismiss, testId = 'data-issues-banner' }) {
  const [open, setOpen] = useState(false);
  const count = issues.length + warnings.length;
  if (count === 0) return null;

  const skipped = issues.length;
  const errored = warnings.length;

  return (
    <div className="rounded-xl border border-amber-700/60 bg-amber-950/30 px-3 py-2 text-amber-200" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-3 text-left"
        data-testid={`${testId}-toggle`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <AlertTriangle size={14} className="text-amber-400 shrink-0" />
          <span className="text-xs font-semibold truncate">
            {skipped > 0 && <>⚠️ {skipped} {skipped === 1 ? 'row' : 'rows'} skipped due to data issues</>}
            {skipped > 0 && errored > 0 && ' · '}
            {errored > 0 && <span className="text-red-300">{errored} block{errored === 1 ? '' : 's'} failed</span>}
          </span>
          <span className="text-[10px] text-amber-400/70 hidden sm:inline">
            (analysis still loaded; fix the bad records to improve accuracy)
          </span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {onDismiss && (
            <span
              onClick={(e) => { e.stopPropagation(); onDismiss(); }}
              className="text-[10px] text-amber-400/70 hover:text-amber-200 cursor-pointer px-1.5"
            >
              Dismiss
            </span>
          )}
          {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        </div>
      </button>

      {open && (
        <div className="mt-2 pt-2 border-t border-amber-800/50 space-y-1.5 max-h-72 overflow-y-auto" data-testid={`${testId}-details`}>
          {warnings.map((w, i) => (
            <div key={`w-${i}`} className="text-[11px] text-red-300 font-mono break-all">
              ⛔ {w}
            </div>
          ))}
          {issues.map((it, i) => {
            const ref = it.ref || {};
            const route = TYPE_ROUTES[ref.type];
            const link = route ? route(ref) : null;
            const levelClass = it.level === 'warning' ? 'text-amber-300' : 'text-amber-400/80';
            return (
              <div key={`i-${i}`} className="flex items-start gap-2 text-[11px]" data-testid={`${testId}-row-${i}`}>
                <span className={`shrink-0 ${levelClass}`}>{it.level === 'warning' ? '⚠️' : 'ℹ️'}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-amber-100">
                    <span className="font-semibold">{it.message}</span>
                  </div>
                  <div className="text-amber-400/80 font-mono break-all">
                    {ref.type && <span className="text-amber-500">{ref.type}</span>}
                    {ref.id && <> · <span className="text-amber-200">{ref.label || ref.id}</span></>}
                    {ref.field && <> · <span className="text-zinc-400">{ref.field}=</span><span className="text-red-300">"{ref.value || '(empty)'}"</span></>}
                  </div>
                </div>
                {link && (
                  <a href={link} className="text-[10px] text-amber-300 hover:text-amber-100 underline inline-flex items-center gap-0.5 shrink-0">
                    Open <ExternalLink size={9}/>
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
